import React, { useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { pathKeys } from '../../constants'
import Loading from '../../components/Loading'
import { useTranslation } from 'react-i18next'
import ChangeLanguage from 'components/ChangeLanguage'
import MessengerButton from '../Home/items/MessengerButton'
import FormDialog from '../../components/FormDialog'

/* ─── inline styles (tránh MUI override) ──────────────────────────────────── */
const S = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    background: '#F9FAFB',
    position: 'relative',
    overflowX: 'hidden',
    fontFamily: "'Be Vietnam Pro', 'Segoe UI', sans-serif",
  },
  /* green blob left */
  blobWrap: {
    position: 'fixed',
    inset: 0,
    zIndex: 0,
    pointerEvents: 'none',
  },
  blobSvg: {
    position: 'absolute',
    left: 0,
    top: 0,
    height: '100%',
    width: '62%',
    minWidth: 620,
  },
  /* robot face bottom-left */
  robotFace: {
    position: 'fixed',
    left: 0,
    bottom: 0,
    width: 360,
    zIndex: 1,
    pointerEvents: 'none',
  },
  /* right panel */
  rightPanel: {
    position: 'relative',
    zIndex: 2,
    width: '100%',
    maxWidth: 460,
    padding: '40px 64px 40px 40px',
  },
  authCard: {
    width: '100%',
    maxWidth: 380,
    marginLeft: 'auto',
  },
  /* logo */
  logoLockup: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 10px',
  },
  logoImg: {
    height: 40,
    width: 'auto',
    display: 'block',
  },
  productTagline: {
    fontSize: 12.5,
    fontWeight: 600,
    color: '#9CA3AF',
    textAlign: 'center',
    letterSpacing: '0.01em',
    marginBottom: 34,
  },
  authTitle: {
    fontSize: 22,
    fontWeight: 800,
    lineHeight: 1.35,
    marginBottom: 8,
    textAlign: 'center',
    color: '#111827',
  },
  authSub: {
    fontSize: 13.5,
    color: '#6B7280',
    lineHeight: 1.6,
    marginBottom: 30,
    textAlign: 'center',
  },
  /* Facebook CTA */
  btnFb: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 11,
    padding: '15px 16px',
    borderRadius: 12,
    border: 'none',
    cursor: 'pointer',
    background: '#1877F2',
    color: '#fff',
    fontFamily: 'inherit',
    fontSize: 15,
    fontWeight: 700,
    boxShadow: '0 8px 20px rgba(24,119,242,0.28)',
    transition: 'background 0.12s',
  },
  btnFbNote: {
    fontSize: 12,
    textAlign: 'center',
    color: '#9CA3AF',
    marginTop: 10,
  },
  /* email toggle */
  emailToggle: {
    display: 'block',
    width: '100%',
    textAlign: 'center',
    marginTop: 26,
    paddingTop: 20,
    borderTop: '1px solid #E5E7EB',
    borderLeft: 'none',
    borderRight: 'none',
    borderBottom: 'none',
    background: 'none',
    fontFamily: 'inherit',
    fontSize: 12,
    fontWeight: 600,
    color: '#9CA3AF',
    cursor: 'pointer',
  },
  emailToggleSpan: {
    textDecoration: 'underline',
    textUnderlineOffset: 2,
  },
  /* email form */
  emailFormHint: {
    fontSize: 11,
    color: '#9CA3AF',
    marginBottom: 12,
    textAlign: 'center',
    lineHeight: 1.5,
  },
  fieldLabel: {
    display: 'block',
    fontSize: 11,
    fontWeight: 600,
    color: '#6B7280',
    marginBottom: 4,
  },
  fieldInput: {
    width: '100%',
    padding: '9px 11px',
    border: '1px solid #E5E7EB',
    borderRadius: 8,
    fontFamily: 'inherit',
    fontSize: 12.5,
    color: '#111827',
    background: '#fff',
    outline: 'none',
    boxSizing: 'border-box',
  },
  fieldRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginBottom: 12,
  },
  forgotLink: {
    fontSize: 11,
    fontWeight: 600,
    color: '#9CA3AF',
    textDecoration: 'none',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    fontFamily: 'inherit',
    padding: 0,
  },
  btnEmailLogin: {
    width: '100%',
    padding: '9px 16px',
    borderRadius: 8,
    cursor: 'pointer',
    background: 'transparent',
    color: '#6B7280',
    border: '1px solid #E5E7EB',
    fontFamily: 'inherit',
    fontSize: 12.5,
    fontWeight: 600,
    transition: 'border-color 0.12s, color 0.12s',
  },
  errMsg: {
    color: '#EF4444',
    fontSize: 12,
    textAlign: 'center',
    marginTop: 10,
  },
  langRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 24,
  },
  /* mobile */
  mobileLogo: {
    display: 'none',
  },
}

/* ─── Responsive: mobile overrides via a <style> tag ─────────────────────── */
const MobileStyle = () => (
  <style>{`
    @media (max-width: 900px) {
      .login-blob-wrap, .login-robot { display: none !important; }
      body { justify-content: center; background: #fff !important; }
      .login-right-panel { max-width: 420px !important; padding: 32px 20px !important; }
      .login-auth-card { margin: 0 auto !important; }
      .login-mobile-logo { display: flex !important; align-items: center; justify-content: center; gap: 8px; margin-bottom: 24px; font-size: 19px; font-weight: 800; }
      .login-mobile-logo .box { background: #22C55E; color: #fff; border-radius: 7px; padding: 2px 8px; }
      .login-logo-lockup, .login-product-tagline { display: none !important; }
    }
    .login-btn-fb:hover { background: #145DBF !important; transform: translateY(-1px); }
    .login-forgot-link:hover { color: #16A34A !important; }
    .login-btn-email:hover { border-color: #22C55E !important; color: #16A34A !important; }
    .login-email-toggle:hover { color: #6B7280 !important; }
  `}</style>
)

const LoginView = (props) => {
  const {
    login,
    getAccount,
    handleSubmitEmail,
    statusLogined,
    onSubmitForgotPassword,
    forgotPassword,
    loading,
  } = props

  const history  = useHistory()
  const location = useLocation()
  const { t, i18n } = useTranslation()

  const [email,                setEmail]                = useState('')
  const [password,             setPassword]             = useState('')
  const [showEmailForm,        setShowEmailForm]        = useState(false)
  const [isForgotPasswordPopup, setIsForgotPasswordPopup] = useState(false)
  const [emailForgotPassword,  setEmailForgotPassword]  = useState('')

  const token = localStorage.getItem('token')
  useEffect(() => { if (token) history.push(pathKeys.HOME) }, [token])

  useEffect(() => { if (forgotPassword) setEmailForgotPassword('') }, [forgotPassword])

  useEffect(() => {
    const searchParam = new URLSearchParams(location.search)
    const getLang = searchParam.get('lang')
    if (getLang && /(vi|en|kh)/g.test(getLang) && i18n.language !== getLang) {
      i18n.changeLanguage(getLang)
    }
  }, [location.search, i18n])

  useEffect(() => {
    if (window.location.hash && window.location.hash.includes('access_token')) {
      const hashParams = new URLSearchParams(window.location.hash.substring(1))
      const accessToken = hashParams.get('access_token')
      if (accessToken) {
        const searchParam = new URLSearchParams(window.location.search)
        const ref = searchParam.get('ref')
        props.loginMobileFB(accessToken, ref)
      }
    }
  }, [])

  const handleClickButtonFacebook = () => {
    localStorage.removeItem('alertGiftBox')
    const searchParam = new URLSearchParams(window.location.search)
    const ref = searchParam.get('ref')
    login(ref)
  }

  const handleSubmit = (e) => {
    if (e) e.preventDefault()
    const searchParam = new URLSearchParams(window.location.search)
    const ref = searchParam.get('ref')
    const payload = { email, password }
    if (ref) payload.ref = ref
    handleSubmitEmail(payload)
  }

  const handleForgotPassword = () => {
    if (emailForgotPassword) {
      onSubmitForgotPassword({ email: emailForgotPassword })
    }
  }

  return (
    <div style={S.page}>
      <MobileStyle />
      {loading && <Loading />}

      {/* ── Green blob ─────────────────────────────────────────────────── */}
      <div style={S.blobWrap} className="login-blob-wrap" aria-hidden="true">
        <svg style={S.blobSvg} viewBox="0 0 900 1080" preserveAspectRatio="xMinYMid slice">
          <path fill="#22C55E" d="M0,0 H620 C700,120 560,260 640,420 C720,580 560,700 640,860 C700,970 600,1080 480,1080 H0 Z"/>
        </svg>
      </div>

      {/* ── Robot mascot ───────────────────────────────────────────────── */}
      <div style={S.robotFace} className="login-robot" aria-hidden="true">
        <img src="/image/bombot-login.svg" alt="" style={{ width: '100%', display: 'block' }}
          onError={(e) => { e.target.style.display = 'none' }} />
      </div>

      {/* ── Right panel ────────────────────────────────────────────────── */}
      <div style={S.rightPanel} className="login-right-panel">
        <div style={S.authCard} className="login-auth-card">

          {/* Mobile logo */}
          <div style={S.mobileLogo} className="login-mobile-logo">
            <span className="box">BOM</span>BOT
          </div>

          {/* Logo lockup */}
          <div style={S.logoLockup} className="login-logo-lockup">
            <img
              src="https://bombot.vn/wp-content/uploads/2021/11/logo-1-e1703665520605.png"
              alt="Bombot"
              style={S.logoImg}
            />
          </div>
          <div style={S.productTagline} className="login-product-tagline">
            {t('login.bulk_messaging_application')}
          </div>

          {/* Auth headline */}
          <div style={S.authTitle}>
            {t('login.sign_in_or_create')}
          </div>
          <div style={S.authSub}>
            {t('login.facebook_auto_register')}
          </div>

          {/* Facebook CTA */}
          <button
            className="login-btn-fb"
            style={S.btnFb}
            type="button"
            onClick={handleClickButtonFacebook}
          >
            <svg viewBox="0 0 24 24" width="21" height="21" fill="#fff">
              <path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5 3.66 9.15 8.44 9.94v-7.03H7.9v-2.91h2.54V9.85c0-2.5 1.49-3.89 3.77-3.89 1.09 0 2.23.2 2.23.2v2.45h-1.26c-1.24 0-1.62.77-1.62 1.56v1.89h2.77l-.44 2.91h-2.33V22c4.78-.79 8.44-4.94 8.44-9.94z"/>
            </svg>
            {t('login.login_by_facebook')}
          </button>
          <div style={S.btnFbNote}>{t('login.facebook_auto_register_note')}</div>

          {/* Email toggle */}
          <button
            className="login-email-toggle"
            style={S.emailToggle}
            type="button"
            onClick={() => setShowEmailForm((v) => !v)}
          >
            {t('login.has_account')} <span style={S.emailToggleSpan}>{t('login.login_by_email')}</span>
          </button>

          {/* Email form */}
          {showEmailForm && (
            <div style={{ marginTop: 16 }}>
              <div style={S.emailFormHint}>{t('login.email_form_hint')}</div>
              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: 10 }}>
                  <label style={S.fieldLabel}>Email</label>
                  <input
                    style={S.fieldInput}
                    type="email"
                    placeholder="ban@congty.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <div style={{ marginBottom: 0 }}>
                  <label style={S.fieldLabel}>{t('update.form.plh_pass')}</label>
                  <input
                    style={S.fieldInput}
                    type="password"
                    placeholder={t('update.form.plh_pass')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
                <div style={S.fieldRow}>
                  <button
                    type="button"
                    className="login-forgot-link"
                    style={S.forgotLink}
                    onClick={() => setIsForgotPasswordPopup(true)}
                  >
                    {t('login.forgot_password.title')}
                  </button>
                </div>
                <button
                  className="login-btn-email"
                  style={S.btnEmailLogin}
                  type="submit"
                >
                  {t('common.btn.login')}
                </button>
              </form>
              {statusLogined === false && (
                <div style={S.errMsg}>{t('noti.login_error')}</div>
              )}
            </div>
          )}

          {/* Language switcher */}
          <div style={S.langRow}>
            <ChangeLanguage />
          </div>
        </div>
      </div>

      {/* ── Forgot password dialog ─────────────────────────────────────── */}
      <FormDialog
        isOpen={isForgotPasswordPopup}
        handleClose={() => setIsForgotPasswordPopup(false)}
      >
        <div style={{ padding: '0 15px 40px' }}>
          <h4 style={{ textAlign: 'center', marginTop: 30, marginBottom: 15, color: '#05AE34' }}>
            {t('login.forgot_password.title')}
          </h4>
          <p style={{ textAlign: 'center', paddingBottom: 10 }}>
            {t('login.forgot_password.description')}
          </p>
          {forgotPassword === false && (
            <p style={{ color: 'red', textAlign: 'center' }}>{t('noti.email_not_exist')}</p>
          )}
          {forgotPassword === true && (
            <p style={{ color: '#05AE34', textAlign: 'center' }}>{t('noti.reset_password')}</p>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            <input
              style={{ ...S.fieldInput, flex: 1 }}
              type="email"
              placeholder="Email"
              value={emailForgotPassword}
              onChange={(e) => setEmailForgotPassword(e.target.value)}
            />
            <button
              style={{
                padding: '9px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
                background: '#FF8000', color: '#fff', fontFamily: 'inherit',
                fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap',
              }}
              onClick={handleForgotPassword}
            >
              {t('common.btn.confirm')}
            </button>
          </div>
        </div>
      </FormDialog>

      <MessengerButton />
    </div>
  )
}

export default LoginView
